import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  IsoDateSelectorComponent,
  IsoTimeSelectorComponent,
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  ISODateType,
  mergeDayAndTime,
  Time24hrType,
  Timestamp,
  Timezone,
  to24hrTime,
  toISODate,
  toMomentTz,
  toTimestamp,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';

export interface IMarketingCampaignNextRunDialogData {
  nextRunAt?: Timestamp;
  message?: string;
  timezone: Timezone;
}

export interface ICampaignNextRunDateSelectorResult {
  nextRunAt: Timestamp;
}

interface ICampaignNextRunFormData {
  recurrenceRunDate: ISODateType;
  recurrenceRunTime: Time24hrType;
}

@Component({
  selector: 'pr-campaign-next-run-date-selector-dialog',
  templateUrl: './campaign-next-run-date-selector-dialog.component.html',
  styleUrl: './campaign-next-run-date-selector-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    InformationBoxComponent,
    IsoDateSelectorComponent,
    IsoTimeSelectorComponent,
  ],
})
export class CampaignNextRunDateSelectorDialogComponent {
  private _dialogRef = inject(
    MatDialogRef<
      CampaignNextRunDateSelectorDialogComponent,
      ICampaignNextRunDateSelectorResult
    >
  );
  public data = inject<IMarketingCampaignNextRunDialogData>(MAT_DIALOG_DATA);
  minDate = moment();

  form = new TypedFormGroup<ICampaignNextRunFormData>({
    recurrenceRunDate: new TypedFormControl<ISODateType>(
      toISODate(moment()),
      Validators.required
    ),
    recurrenceRunTime: new TypedFormControl<Time24hrType>(
      to24hrTime(moment()),
      Validators.required
    ),
  });

  constructor() {
    if (this.data.nextRunAt) {
      const nextRunAt = toMomentTz(this.data.nextRunAt, this.data.timezone);
      this.form.patchValue({
        recurrenceRunDate: toISODate(nextRunAt),
        recurrenceRunTime: to24hrTime(nextRunAt),
      });
    }
  }

  submit(): void {
    if (this.form.invalid) {
      return;
    }

    const { recurrenceRunDate, recurrenceRunTime } = this.form.value;
    const nextRunAt = mergeDayAndTime(
      toMomentTz(recurrenceRunDate, this.data.timezone),
      recurrenceRunTime,
      this.data.timezone
    );

    this._dialogRef.close({
      nextRunAt: toTimestamp(nextRunAt),
    });
  }
}
